/**
 * The welcome (issue #130, after ICP Hub's first login; owner's corrections 2026-10-13: not a checklist,
 * and jobs rather than menu entries): a title, «Hva vil du gjøre først?», a row per job the person may
 * do (icon, title, one line, chevron) that starts it, a close cross and «Utforsk på egen hånd». Shown once to a company administrator on
 * the first visit after setup (user_tenants.welcomed_at, GET /tenant me.welcomed_at, POST
 * /welcome/seen); in a demo only after Hugin's opening question has been shown and put away, so the
 * two never overlap. «Kom i gang» in the help menu opens it again at any time.
 */
import { post } from '@/api/client';
import { useOnboarding } from '@/api/onboarding';
import { useProjectWord, useProjects, useProjectsOn } from '@/api/projects';
import { useCan } from '@/auth/caps';
import { useAuth } from '@/auth/store';
import { T } from '@/i18n';
import { track } from '@/marketing/analytics';
import { type WelcomeKey, countAppPage } from '@/marketing/count';
import { usePowerOffice } from '@/pages/lonn/PowerOffice';
import { useTripletex } from '@/pages/lonn/Tripletex';
import { toVaktplan } from '@/pages/vaktplan/link';
import { draftForHugin, useHuginOpening } from '@/poc-chat/Panel';
import { today } from '@/rules';
import { useTenant } from '@/shell/tenant';
import { Icon, type IconName } from '@/ui/icons';
import * as Dialog from '@radix-ui/react-dialog';
import { mondayOf } from '@timefly/core';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { create } from 'zustand';

/** Opened from the help menu («Kom i gang»); the first-visit opening needs no store. */
export const useWelcome = create<{ open: boolean; show: () => void; hide: () => void }>((set) => ({
  open: false,
  show: () => set({ open: true }),
  hide: () => set({ open: false }),
}));

interface Row {
  key: WelcomeKey;
  icon: IconName;
  title: string;
  line: string;
  /** the page with the job set up; or, for Hugin, the request typed into its box (not sent) */
  to?: string;
  ask?: string;
  /** the person may open the page and do the job: the capabilities, and the workflow being on */
  shows: boolean;
}

/**
 * The jobs a manager wants done first, in their words, each starting that job (owner's second
 * correction, 2026-10-13: «think from the user's perspective what they would like to do, not just
 * list available navigation points»). By the company's mode and this person's permissions.
 */
export function useWelcomeRows(): Row[] {
  const { mode, tenant } = useTenant();
  const can = useCan();
  // a company that chose Tripletex or PowerOffice in Oppsett and skipped the key connects from here
  // (owner, 2026-10-21: «if they skip the step it has to work after»)
  const sys = tenant?.payroll_system;
  const mayConnect = can('innstillinger.endre');
  const tx = useTripletex(sys === 'tripletex' && mayConnect);
  const po = usePowerOffice(sys === 'poweroffice' && mayConnect);
  const link = sys === 'tripletex' ? tx.data : sys === 'poweroffice' ? po.data : undefined;
  const connect: Row = {
    key: 'koble',
    icon: 'banknote',
    title: T('Koble til {system}', { system: sys === 'poweroffice' ? 'PowerOffice Go' : 'Tripletex' }),
    line: T('Så henter vi ansatte og lønnsarter, og timene går rett til lønn.'),
    to: `/innstillinger/lonn#${sys === 'poweroffice' ? 'poweroffice' : 'tripletex'}`,
    shows: mayConnect && !!link?.available && !link.connected,
  };
  const projectsOn = useProjectsOn();
  const word = useProjectWord();
  const firstProject = (useProjects().data ?? []).find((p) => p.active)?.name;
  const monday = mondayOf(today());
  const rows: Row[] = [connect].concat(
    mode === 'rota'
      ? [
          {
            key: 'plan',
            icon: 'chat',
            title: T('Lag neste ukes vaktplan'),
            line: T('Hugin lager et utkast, du godkjenner.'),
            ask: T('Lag vaktplan for neste uke'),
            shows: can('assistent.bruke') && can('vaktplan.endre'),
          },
          {
            key: 'stempling',
            icon: 'clock',
            title: T('Se hvem som mangler stempling i dag'),
            line: T('Rett opp før lønna.'),
            to: '/hjem?vis=avklar',
            shows: can('timeliste.se'),
          },
          {
            key: 'kost',
            icon: 'percent',
            title: T('Sjekk hva uka koster mot salget'),
            line: T('Lønnsprosent per dag og uke.'),
            to: '/omsetning',
            shows: can('vaktplan.kost'),
          },
          {
            key: 'ledig',
            icon: 'calendar',
            title: T('Fyll en ledig vakt'),
            line: T('Se hvem som kan ta den, og gi dem vakten.'),
            to: toVaktplan({ week: monday, open: true }),
            shows: can('vaktplan.endre'),
          },
          {
            key: 'godkjenn',
            icon: 'check',
            title: T('Godkjenn timene og send dem til regnskap'),
            line: T('Excel, PDF eller rett i Tripletex.'),
            to: '/timeliste?vis=klare',
            shows: can('timeliste.godkjenne'),
          },
          {
            key: 'app',
            icon: 'phone',
            title: T('Få de ansatte inn i appen'),
            line: T('Send invitasjon til dem som ikke har logget inn.'),
            to: '/ansatte?app=ikke',
            shows: can('ansatte.endre'),
          },
        ]
      : [
          {
            key: 'registrer',
            icon: 'plus',
            title: projectsOn
              ? T('Registrer timer på et {word}', { word: word.lower })
              : T('Registrer timer for en ansatt'),
            line: T('Eller la de ansatte stemple inn på telefonen.'),
            to: '/timeliste?ny=registrering',
            shows: can('timeliste.rette'),
          },
          {
            key: 'kunder',
            icon: 'folder',
            title: T('Se timer per kunde denne måneden'),
            line: T('Summert per kunde og {word}, som CSV, Excel eller PDF.', { word: word.lower }),
            to: '/prosjekter?vis=kunder',
            shows: projectsOn && can('prosjekter.se'),
          },
          {
            key: 'rapport',
            icon: 'document',
            title: T('Lag timerapport til en kunde'),
            line: T('PDF eller Excel med timer per dato og person.'),
            to: '/prosjekter?vis=rapport',
            shows: projectsOn && can('prosjekter.se'),
          },
          {
            key: 'godkjenn',
            icon: 'check',
            title: T('Godkjenn ukas timer'),
            line: T('Se de klare registreringene og godkjenn dem.'),
            to: `/timeliste?vis=klare&uke=${monday}`,
            shows: can('timeliste.godkjenne'),
          },
          {
            key: 'send',
            icon: 'download',
            title: T('Send måneden til regnskapsføreren'),
            line: T('Excel, PDF eller Tripletex-fil.'),
            to: '/lonn?vis=eksport',
            shows: can('timerloenn.se'),
          },
          {
            key: 'hugin',
            icon: 'chat',
            title: T('Spør Hugin om timene'),
            line: T('For eksempel: {q}', {
              q: firstProject
                ? T('Hvem har jobbet mest på {name}?', { name: firstProject })
                : T('Hvem har jobbet mest denne måneden?'),
            }),
            ask: firstProject
              ? T('Hvem har jobbet mest på {name}?', { name: firstProject })
              : T('Hvem har jobbet mest denne måneden?'),
            shows: can('assistent.bruke'),
          },
        ],
  );
  return rows.filter((r) => r.shows);
}

export function Welcome() {
  const { tenant } = useTenant();
  const can = useCan();
  const nav = useNavigate();
  const supporting = useAuth((s) => !!s.user?.support);
  const fromHelp = useWelcome((s) => s.open);
  const hugin = useHuginOpening((s) => s.state);
  // closed in this visit, per company: a switch to another company that has not seen it still shows it there
  const [closedFor, setClosedFor] = useState<number | null>(null);
  const admin = tenant?.me?.is_admin === true;
  // a demo opens with Hugin's question (#101); the overview waits until that has been shown and put away
  const huginFirst = !!tenant?.is_demo && can('assistent.bruke') && hugin !== 'done';
  const first =
    !!tenant &&
    admin &&
    !supporting &&
    !tenant.setup_step &&
    // strictly null: an older server that does not answer the field shows nothing
    tenant.me?.welcomed_at === null &&
    closedFor !== tenant.id &&
    !huginFirst;
  const open = admin && !supporting && (fromHelp || first);
  const kind = tenant?.is_demo ? 'demo' : 'bedrift';

  // counted once each time it opens: PostHog with consent, and the cookie-free page count for everybody
  // biome-ignore lint/correctness/useExhaustiveDependencies: the opening is the event, not a change of company data
  useEffect(() => {
    if (!open) return;
    track('welcome_shown', { from: fromHelp ? 'help' : 'first', workspace_kind: kind });
    countAppPage('welcome', kind);
  }, [open]);

  if (!open || !tenant) return null;
  const close = () => {
    setClosedFor(tenant.id);
    useWelcome.getState().hide();
    // the first close marks it seen; a refused or failed mark only means it shows once more next visit
    if (tenant.me?.welcomed_at == null) post('/welcome/seen', {}).catch(() => {});
  };
  const press = (r: Row) => {
    track('welcome_step_opened', { step: r.key, workspace_kind: kind });
    countAppPage(`welcome-${r.key}`, kind);
    close();
    if (r.to) nav(r.to);
    if (r.ask) draftForHugin(r.ask);
  };
  return <Sheet onClose={close} onPress={press} />;
}

/** The dialog itself, mounted only while open, so its rows' reads (the projects) run only then. */
function Sheet({ onClose: close, onPress: press }: { onClose: () => void; onPress: (r: Row) => void }) {
  const rows = useWelcomeRows();
  // how far the company is, from the same saved facts as Kom i gang (#294 ONB-01); nothing while unknown
  const steps = useOnboarding().data?.steps;
  const done = steps?.filter((x) => x.done).length ?? 0;
  const nav = useNavigate();
  return (
    <Dialog.Root open onOpenChange={(o) => !o && close()}>
      <Dialog.Portal>
        <Dialog.Overlay className="scrim" />
        <Dialog.Content className="welcome" aria-describedby="welcome-line" data-testid="welcome">
          <Dialog.Close asChild>
            <button type="button" className="x" aria-label={T('Lukk')}>
              <Icon name="close" />
            </button>
          </Dialog.Close>
          <Dialog.Title asChild>
            <h2>{T('Velkommen til Timefly!')}</h2>
          </Dialog.Title>
          <p id="welcome-line">{T('Hva vil du gjøre først? Velg en oppgave for å komme i gang.')}</p>
          {steps && done < steps.length && (
            <button
              type="button"
              className="wnext"
              data-testid="welcome-komigang"
              onClick={() => {
                close();
                nav('/kom-i-gang');
              }}
            >
              <span className="wi">
                <Icon name="flag" />
              </span>
              <span className="wt">
                <b>{T('Kom i gang')}</b>
                <span>
                  {T('{done} av {all} steg er ferdige. Se hva som gjenstår.', { done, all: steps.length })}
                </span>
              </span>
              <span className="wc">
                <Icon name="chevronRight" />
              </span>
            </button>
          )}
          <ul>
            {rows.map((r) => (
              <li key={r.key}>
                <button type="button" data-testid={`welcome-${r.key}`} onClick={() => press(r)}>
                  <span className="wi">
                    <Icon name={r.icon} />
                  </span>
                  <span className="wt">
                    <b>{r.title}</b>
                    <span>{r.line}</span>
                  </span>
                  <span className="wc">
                    <Icon name="chevronRight" />
                  </span>
                </button>
              </li>
            ))}
          </ul>
          <button type="button" className="btn pri wgo" onClick={close}>
            {T('Utforsk på egen hånd')}
          </button>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
